import { CloseCircleTwoTone } from '@ant-design/icons';
import { Alert, Button, Checkbox, Col, Form, Input, Modal, Row, Select } from 'antd';
import { AxiosError } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';

import { elementWithLoading } from '../../../components/LoadingWrapper';
import { PermissionUsageMR, permissionsService, Values } from '../../../services/permission';
import { PlatformEnum } from '../../../types/enums';
import { confirmClose } from '../../../utils/confirmClose';
import { useEffectTrigger } from '../../../utils/hooks/useEffectTrigger';
import { useForm } from '../../../utils/hooks/useForm';
import { openSuccessNotification } from '../../../utils/notifications';
import { showApiErrors } from '../../../utils/showApiErrors';

type AddOrEditModalProps = {
  modalVisible: boolean;
  permission?: PermissionUsageMR;
  onCancel: () => void;
  onSuccess: () => void;
};

type AttributeRow = {
  name: string;
  type: 'str' | 'bool';
  multi?: boolean;
  values?: string[];
};

type FormValues = {
  name: string;
  platform: PlatformEnum;
  attributes?: AttributeRow[];
};

const ATTRIBUTE_TYPE_OPTIONS = [
  { value: 'str', label: 'str' },
  { value: 'bool', label: 'bool' }
];

const PLATFORM_OPTIONS = Object.values(PlatformEnum).map((platform) => ({ value: platform, label: platform }));

export const AddOrEditModal = ({ modalVisible, permission, onCancel, onSuccess }: AddOrEditModalProps): JSX.Element => {
  const [, activateFormChangedTrigger] = useEffectTrigger();

  const [form] = useForm(activateFormChangedTrigger);

  const [wasFormChanged, setWasFormChanged] = useState(false);
  const [loading, setLoading] = useState(false);

  const isEdit = !!permission;
  const readOnly = permission?.isProtected ?? false;
  // Rows seeded before the attribute schema existed can hold params this form
  // cannot represent, and saving would replace them.
  const hasUneditableParams = !!permission?.params && !permission.params.attributes;

  const titleAndBtnText = `${isEdit ? 'Edit' : 'Add'} Permission`;

  const handleCancel = () => {
    wasFormChanged ? confirmClose(onCancel) : onCancel();
  };

  const initialValues = useMemo<Partial<FormValues>>(() => {
    if (!permission) {
      return { attributes: [] };
    }

    return {
      name: permission.name,
      platform: permission.platform,
      attributes: (permission.params?.attributes ?? []).map((attribute) => ({
        name: attribute.name,
        type: attribute.type,
        multi: attribute.multi ?? false,
        values: attribute.values ?? []
      }))
    };
  }, [permission]);

  useEffect(() => {
    form.resetFields();
    setWasFormChanged(false);
  }, [form, modalVisible, initialValues]);

  const parseAttributes = (rows: AttributeRow[]): Values[] =>
    rows.map((row) => {
      const attribute: Values = { name: row.name.trim(), type: row.type };

      if (row.type === 'str') {
        if (row.multi) {
          attribute.multi = true;
        }

        if (row.values?.length) {
          attribute.values = row.values;
        }
      }

      return attribute;
    });

  const handleSubmit = async (values: FormValues) => {
    setLoading(true);
    try {
      const attributes = parseAttributes(values.attributes ?? []);
      const params = attributes.length ? { attributes } : null;

      if (permission) {
        await permissionsService.edit(permission.id, { params });
        openSuccessNotification({ message: 'Permission successfully edited!' });
      } else {
        await permissionsService.add({ name: values.name.trim(), platform: values.platform, params });
        openSuccessNotification({ message: 'Permission successfully added!' });
      }

      onSuccess();
    } catch (e) {
      showApiErrors(e as AxiosError);
    } finally {
      setLoading(false);
    }
  };

  const formElement = (
    <Form
      form={form}
      onFinish={(values) => void handleSubmit(values as FormValues)}
      onFinishFailed={({ errorFields }) => {
        form.scrollToField(errorFields[0].name);
      }}
      onValuesChange={() => {
        setWasFormChanged(true);
        activateFormChangedTrigger();
      }}
      initialValues={initialValues}
      layout="vertical"
    >
      {readOnly && (
        <Alert
          className="mb-4"
          type="warning"
          showIcon
          message="IAM authorizes its own routes with this permission, so it cannot be changed or deleted."
        />
      )}

      {hasUneditableParams && (
        <Alert
          className="mb-4"
          type="warning"
          showIcon
          message="This permission has parameters in a format this form cannot show. Saving replaces them."
        />
      )}

      <Row gutter={32}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="name"
            label="Name"
            extra={isEdit ? 'Apps check permissions by name, so it cannot be renamed.' : undefined}
            rules={[
              {
                required: true,
                message: 'Please input name!'
              }
            ]}
          >
            <Input placeholder="e.g. mcp.access" disabled={isEdit} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="platform"
            label="Platform"
            rules={[
              {
                required: true,
                message: 'Please select platform!'
              }
            ]}
          >
            <Select placeholder="Platform" options={PLATFORM_OPTIONS} disabled={isEdit} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <h3>Attributes</h3>
      <p className="text-grey-secondary">
        Parameters a role fills in when it grants this permission. Leave empty for a plain on/off permission.
      </p>

      <Form.List name="attributes">
        {(fields, { add, remove }) => (
          <div>
            {fields.map((field) => {
              const type = form.getFieldValue(['attributes', field.name, 'type']);

              return (
                <Row key={field.key} gutter={8}>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      name={[field.name, 'name']}
                      label="Name"
                      rules={[{ required: true, message: 'Please input attribute name!' }]}
                    >
                      <Input placeholder="e.g. db" disabled={readOnly} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={4}>
                    <Form.Item
                      name={[field.name, 'type']}
                      label="Type"
                      rules={[{ required: true, message: 'Please select type!' }]}
                    >
                      <Select options={ATTRIBUTE_TYPE_OPTIONS} disabled={readOnly} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={9}>
                    {type === 'str' && (
                      <Form.Item name={[field.name, 'values']} label="Allowed values">
                        <Select
                          mode="tags"
                          placeholder="Type a value and press enter"
                          disabled={readOnly}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    )}
                  </Col>
                  <Col xs={12} sm={3}>
                    {type === 'str' && (
                      <Form.Item name={[field.name, 'multi']} label="Multiple" valuePropName="checked">
                        <Checkbox disabled={readOnly} />
                      </Form.Item>
                    )}
                  </Col>
                  <Col xs={12} sm={2} style={{ justifyContent: 'end', display: 'flex', alignItems: 'center' }}>
                    {!readOnly && (
                      <CloseCircleTwoTone
                        twoToneColor="#FF6347"
                        onClick={() => {
                          remove(field.name);
                          setWasFormChanged(true);
                        }}
                      />
                    )}
                  </Col>
                </Row>
              );
            })}

            {!readOnly && (
              <Button
                onClick={() => {
                  add({ type: 'str' });
                  setWasFormChanged(true);
                }}
              >
                Add attribute
              </Button>
            )}
          </div>
        )}
      </Form.List>

      {!readOnly && (
        <Row gutter={32}>
          <Col span={24} style={{ display: 'flex', justifyContent: 'end', marginTop: '24px' }}>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                {titleAndBtnText}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      )}
    </Form>
  );

  return (
    <Modal
      title={titleAndBtnText}
      wrapClassName="vertical-center-modal"
      open={modalVisible}
      onCancel={handleCancel}
      footer={null}
      width="80%"
    >
      {elementWithLoading(formElement, loading)}
    </Modal>
  );
};
